/**
 * Dominant-color sampling for the "living color" ambient glow. Pure helpers (averageColor, vivify)
 * are unit tested; the canvas/Image parts are thin and fail safe to the brand gold so the glow
 * never breaks rendering. House style: no em dashes.
 */

export interface RGB {
  r: number;
  g: number;
  b: number;
}

/** Brand gold, the fallback whenever sampling is unavailable or fails. */
export const GOLD: RGB = { r: 238, g: 194, b: 91 };

/** Average the visible (non-transparent) pixels of an RGBA buffer. Null if nothing usable. */
export function averageColor(data: Uint8ClampedArray | number[]): RGB | null {
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  for (let i = 0; i + 3 < data.length; i += 4) {
    if (data[i + 3] < 128) continue; // skip near-transparent
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
    n += 1;
  }
  if (!n) return null;
  return { r: Math.round(r / n), g: Math.round(g / n), b: Math.round(b / n) };
}

/** Make a flat average read better as a glow on a dark UI: push saturation a touch and lift very
 *  dark colors so the ambient is visible. Pure and deterministic. */
export function vivify(c: RGB): RGB {
  const mean = (c.r + c.g + c.b) / 3;
  const push = (v: number) => Math.round(Math.min(255, Math.max(0, v + (v - mean) * 0.45)));
  let r = push(c.r);
  let g = push(c.g);
  let b = push(c.b);
  const lum = (r + g + b) / 3;
  if (lum < 80) {
    const lift = 80 - lum;
    r = Math.min(255, r + lift);
    g = Math.min(255, g + lift);
    b = Math.min(255, b + lift);
  }
  return { r, g, b };
}

const cache = new Map<string, RGB>();

/** Sample a loaded image element via a tiny canvas. Returns null on a tainted canvas or any error. */
export function sampleImage(img: HTMLImageElement, size = 16): RGB | null {
  try {
    if (typeof document === "undefined") return null;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, size, size);
    const avg = averageColor(ctx.getImageData(0, 0, size, size).data);
    return avg ? vivify(avg) : null;
  } catch {
    return null; // cross-origin taint, decode failure, etc.
  }
}

/** Load `url` (CORS) and resolve its dominant color, or null on failure. Cached per URL. */
export function loadDominantColor(url: string): Promise<RGB | null> {
  if (!url || typeof Image === "undefined") return Promise.resolve(null);
  const hit = cache.get(url);
  if (hit) return Promise.resolve(hit);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => {
      const c = sampleImage(img);
      if (c) cache.set(url, c);
      resolve(c);
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}
